import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface ProductSummary {
  ndc: string;
  steward_count: number;
  found_count: number;
  error_count: number;
  not_found_count: number;
  other_count: number;
  last_collected: string | null;
}

interface StewardEvidence {
  source_key: string;
  steward_id: string;
  status: string | null;
  collected_at: string | null;
  evidence: Record<string, unknown>;
}

interface EvidenceDetail {
  ndc: string;
  steward_count: number;
  stewards: Record<string, StewardEvidence>;
}

// ============================================================
// HELPERS
// ============================================================

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function statusBadge(status: string | null): { bg: string; color: string; label: string } {
  switch (status) {
    case 'found':
      return { bg: '#DCFCE7', color: '#16A34A', label: 'Found' };
    case 'not_found':
      return { bg: '#FEF3C7', color: '#D97706', label: 'Not Found' };
    case 'error':
      return { bg: '#FEE2E2', color: '#DC2626', label: 'Error' };
    case 'not_loaded':
      return { bg: '#F3F4F6', color: '#6B7280', label: 'Not Loaded' };
    case 'skipped':
      return { bg: '#F3F4F6', color: '#6B7280', label: 'Skipped' };
    default:
      return { bg: '#F3F4F6', color: '#9CA3AF', label: status || 'Unknown' };
  }
}

function formatDateGroup(ts: string | null): string {
  if (!ts) return 'Unknown Date';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function getDateGroupKey(ts: string | null): string {
  if (!ts) return 'unknown';
  try {
    // Group by minute-level precision (most runs happen together)
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return 'unknown';
  }
}

function friendlyStewardName(sourceKey: string): string {
  return sourceKey
    .replace(/^steward_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

// ============================================================
// COMPONENT
// ============================================================

export default function EvidenceHistory() {
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Expanded row state
  const [expandedNdc, setExpandedNdc] = useState<string | null>(null);
  const [evidenceDetail, setEvidenceDetail] = useState<EvidenceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch product list
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/steward-evidence-history?limit=200`, {
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch evidence history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch steward evidence for expanded row
  const fetchEvidence = useCallback(async (ndc: string) => {
    setDetailLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/steward-evidence/${ndc}`, {
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data: EvidenceDetail = await resp.json();
      setEvidenceDetail(data);
    } catch (e: unknown) {
      setEvidenceDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleRowClick = (ndc: string) => {
    if (expandedNdc === ndc) {
      setExpandedNdc(null);
      setEvidenceDetail(null);
    } else {
      setExpandedNdc(ndc);
      fetchEvidence(ndc);
    }
  };

  // Render evidence detail for a single steward
  const renderEvidenceCard = (steward: StewardEvidence) => {
    const badge = statusBadge(steward.status);
    const evidence = steward.evidence || {};

    // Build key-value pairs from evidence, excluding 'status' (shown in badge)
    const entries = Object.entries(evidence).filter(([k]) => k !== 'status');

    return (
      <div
        key={steward.source_key}
        style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          padding: 16,
          marginBottom: 10,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>
              {friendlyStewardName(steward.source_key)}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
              background: badge.bg, color: badge.color, textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              {badge.label}
            </span>
          </div>
          <span style={{ fontSize: 11, color: T.textLight }}>
            {formatTimestamp(steward.collected_at)}
          </span>
        </div>

        {/* Evidence data */}
        {entries.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {entries.map(([key, value]) => (
              <div key={key} style={{ display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.5 }}>
                <span style={{
                  minWidth: isMobile ? 100 : 160, fontWeight: 600, color: T.textLight,
                  textTransform: 'capitalize',
                }}>
                  {key.replace(/_/g, ' ')}
                </span>
                <span style={{ color: T.textDark, wordBreak: 'break-word', flex: 1 }}>
                  {renderValue(value)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: T.textLight, fontStyle: 'italic' }}>No additional data</span>
        )}
      </div>
    );
  };

  // Render a value — handles objects, arrays, primitives
  const renderValue = (value: unknown): React.ReactNode => {
    if (value === null || value === undefined) return <span style={{ color: '#9CA3AF' }}>—</span>;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
      // Check if it's a URL
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#2563EB', textDecoration: 'underline' }}
          >
            {value.length > 60 ? value.substring(0, 60) + '...' : value}
          </a>
        );
      }
      return value;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: '#9CA3AF' }}>[]</span>;
      // If array of primitives, join them
      if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
        return value.join(', ');
      }
      // Array of objects — render as JSON block
      return (
        <pre style={{
          margin: 0, fontSize: 11, background: '#F8FAFC', padding: 8,
          borderRadius: 6, overflowX: 'auto', maxHeight: 200,
          border: '1px solid #E2E8F0',
        }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    if (typeof value === 'object') {
      // Render nested object as a mini key-value list
      const obj = value as Record<string, unknown>;
      const objEntries = Object.entries(obj);
      if (objEntries.length === 0) return <span style={{ color: '#9CA3AF' }}>{'{}'}</span>;

      // For small objects, render inline key-value pairs
      if (objEntries.length <= 6) {
        return (
          <div style={{
            background: '#F8FAFC', borderRadius: 6, padding: '6px 10px',
            border: '1px solid #E2E8F0',
          }}>
            {objEntries.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 6, fontSize: 11, lineHeight: 1.6 }}>
                <span style={{ fontWeight: 600, color: '#64748B', minWidth: 100 }}>
                  {k.replace(/_/g, ' ')}
                </span>
                <span style={{ color: '#334155' }}>{renderValue(v)}</span>
              </div>
            ))}
          </div>
        );
      }

      // Larger objects — render as JSON
      return (
        <pre style={{
          margin: 0, fontSize: 11, background: '#F8FAFC', padding: 8,
          borderRadius: 6, overflowX: 'auto', maxHeight: 200,
          border: '1px solid #E2E8F0',
        }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    return String(value);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${T.border}`,
            borderTopColor: T.gold, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading Evidence History...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Back link + Header */}
      <div>
        <button
          onClick={() => navigate('/agent-detail/data_orchestrator')}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Case Officer
        </button>

        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                  <path d="M12 8v4l3 3" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </div>
              <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, margin: 0, fontFamily: "'Playfair Display', Georgia, serif" }}>
                Evidence History
              </h1>
            </div>
            <p style={{ fontSize: 14, color: T.textMid, maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              Browse all products processed by the Case Officer. Click any row to view detailed agent evidence.
            </p>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <div style={{
              background: `${T.navy}08`, borderRadius: 10, padding: '12px 20px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: T.textDark }}>{total}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Products
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8,
          background: '#FEF2F2', border: '1px solid #FCA5A530',
          color: '#DC2626', fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* Product table */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
        boxShadow: T.shadow, overflow: 'hidden',
      }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 60px 80px' : '1fr 100px 80px 80px 80px 160px',
          padding: '12px 20px',
          background: T.offWhite,
          borderBottom: `1px solid ${T.border}`,
          fontSize: 10, fontWeight: 700, color: T.textLight,
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <span>NDC</span>
          <span style={{ textAlign: 'center' }}>Agents</span>
          {!isMobile && <span style={{ textAlign: 'center' }}>Found</span>}
          {!isMobile && <span style={{ textAlign: 'center' }}>Not Found</span>}
          {!isMobile && <span style={{ textAlign: 'center' }}>Errors</span>}
          <span style={{ textAlign: 'right' }}>Last Collected</span>
        </div>

        {/* Rows */}
        {products.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.textLight, fontSize: 14 }}>
            No evidence data collected yet. Run the Case Officer to populate this page.
          </div>
        ) : (
          products.map(product => (
            <React.Fragment key={product.ndc}>
              {/* Product row */}
              <div
                onClick={() => handleRowClick(product.ndc)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 60px 80px' : '1fr 100px 80px 80px 80px 160px',
                  padding: '14px 20px',
                  borderBottom: `1px solid ${T.border}`,
                  cursor: 'pointer',
                  background: expandedNdc === product.ndc ? '#F8FAFC' : T.white,
                  transition: 'background 0.15s',
                  alignItems: 'center',
                }}
                onMouseEnter={e => {
                  if (expandedNdc !== product.ndc) (e.currentTarget as HTMLDivElement).style.background = '#FAFBFC';
                }}
                onMouseLeave={e => {
                  if (expandedNdc !== product.ndc) (e.currentTarget as HTMLDivElement).style.background = T.white;
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke={T.textLight} strokeWidth="2"
                    style={{
                      transition: 'transform 0.2s',
                      transform: expandedNdc === product.ndc ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark, fontFamily: 'monospace' }}>
                    {product.ndc}
                  </span>
                </div>
                <span style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: T.textDark }}>
                  {product.steward_count}
                </span>
                {!isMobile && (
                  <span style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#16A34A' }}>
                    {product.found_count}
                  </span>
                )}
                {!isMobile && (
                  <span style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#D97706' }}>
                    {product.not_found_count}
                  </span>
                )}
                {!isMobile && (
                  <span style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: product.error_count > 0 ? '#DC2626' : T.textLight }}>
                    {product.error_count}
                  </span>
                )}
                <span style={{ textAlign: 'right', fontSize: 11, color: T.textLight }}>
                  {formatTimestamp(product.last_collected)}
                </span>
              </div>

              {/* Expanded evidence detail */}
              {expandedNdc === product.ndc && (
                <div style={{
                  padding: isMobile ? '16px' : '20px 28px',
                  background: '#F8FAFC',
                  borderBottom: `1px solid ${T.border}`,
                }}>
                  {detailLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 20 }}>
                      <div style={{
                        width: 20, height: 20, border: `2px solid ${T.border}`,
                        borderTopColor: T.gold, borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                      <span style={{ fontSize: 13, color: T.textLight }}>Loading agent evidence...</span>
                    </div>
                  ) : evidenceDetail ? (
                    <div>
                      {/* Steward evidence cards grouped by collection date */}
                      {(() => {
                        const stewards = Object.values(evidenceDetail.stewards);
                        // Group by collection date (minute-level)
                        const groups = new Map<string, { label: string; sortKey: string; stewards: StewardEvidence[] }>();
                        for (const s of stewards) {
                          const key = getDateGroupKey(s.collected_at);
                          if (!groups.has(key)) {
                            groups.set(key, { label: formatDateGroup(s.collected_at), sortKey: key, stewards: [] });
                          }
                          groups.get(key)!.stewards.push(s);
                        }
                        // Sort groups newest first
                        const sortedGroups = Array.from(groups.values()).sort((a, b) => b.sortKey.localeCompare(a.sortKey));

                        return sortedGroups.map((group, gi) => {
                          const groupFound = group.stewards.filter(s => s.status === 'found').length;
                          const groupNotFound = group.stewards.filter(s => s.status === 'not_found').length;
                          const groupErrors = group.stewards.filter(s => s.status === 'error').length;

                          return (
                          <div key={group.sortKey} style={{ marginBottom: gi < sortedGroups.length - 1 ? 24 : 0 }}>
                            {/* Date group header with per-group stats */}
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                              paddingBottom: 8, borderBottom: `1px solid ${T.border}`,
                              flexWrap: 'wrap',
                            }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 6v6l4 2" />
                              </svg>
                              <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>
                                {group.label}
                              </span>
                              <span style={{ fontSize: 11, color: T.textLight }}>
                                {group.stewards.length} agent{group.stewards.length !== 1 ? 's' : ''}
                              </span>
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                                background: '#DCFCE7', color: '#16A34A',
                              }}>
                                {groupFound} Found
                              </span>
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                                background: '#FEF3C7', color: '#D97706',
                              }}>
                                {groupNotFound} Not Found
                              </span>
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                                background: '#FEE2E2', color: '#DC2626',
                              }}>
                                {groupErrors} Errors
                              </span>
                            </div>
                            {/* Cards in this date group */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                              gap: 10,
                            }}>
                              {group.stewards
                                .sort((a, b) => {
                                  const order: Record<string, number> = { found: 0, not_found: 1, error: 2 };
                                  const aO = order[a.status || ''] ?? 3;
                                  const bO = order[b.status || ''] ?? 3;
                                  return aO - bO;
                                })
                                .map(steward => renderEvidenceCard(steward))}
                            </div>
                          </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <div style={{ padding: 20, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
                      Failed to load agent evidence.
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
}
